import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FaTimes } from 'react-icons/fa';
import ConfirmModal from '../../common/ConfirmModal';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../owners/formatMoney';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
        boxShadow: 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: '38px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    menu: (base) => ({ ...base, zIndex: 60 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff3f0' : 'white',
        color: state.isSelected ? 'white' : '#1f2937',
    }),
};

const C2BBulkPostModal = ({ isOpen, rows = [], filters = {}, matchingTotal = 0, matchingTotalAmount = 0, onClose, onSuccess }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || '';

    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [scope, setScope] = useState('selected');
    const [error, setError] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [posting, setPosting] = useState(false);

    const scopeOptions = useMemo(() => ([
        { value: 'selected', label: 'Selected rows on this page' },
        { value: 'all_matching', label: 'All matching filtered transactions' },
    ]), []);

    const totalAmount = useMemo(() => {
        return rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    }, [rows]);

    const selectedAccount = useMemo(() => {
        return accounts.find((account) => String(account.id) === String(selectedAccountId)) || null;
    }, [accounts, selectedAccountId]);

    const selectedAccountOption = useMemo(() => {
        return accounts.map((account) => ({
            value: String(account.id),
            label: `${account.account_name}${account.account_type ? ` (${account.account_type})` : ''}`,
        })).find((option) => option.value === String(selectedAccountId)) || null;
    }, [accounts, selectedAccountId]);

    const selectedWeekLabels = useMemo(() => {
        const labels = Array.from(new Set(rows.map((row) => row.week_key).filter(Boolean)));
        return labels.join(', ');
    }, [rows]);

    const scopeWeekLabels = scope === 'all_matching'
        ? (filters.week_key || [filters.from, filters.to].filter(Boolean).join(' to ') || '—')
        : (selectedWeekLabels || '—');

    const rowsToPostCount = scope === 'all_matching' ? matchingTotal : rows.length;
    const rowsToPostLabel = scope === 'all_matching' ? 'matching rows' : 'selected rows';
    const rowsInSummaryLabel = scope === 'all_matching' ? 'Matching rows' : 'Selected rows';
    const batchTotalAmount = scope === 'all_matching' ? matchingTotalAmount : totalAmount;
    const modalTitle = scope === 'all_matching' ? 'Post matching C2B rows' : 'Post selected C2B rows';
    const modalDescription = scope === 'all_matching'
        ? `Post ${rowsToPostCount} matching C2B transaction${rowsToPostCount === 1 ? '' : 's'} across all pages`
        : `Post ${rows.length} selected C2B transaction${rows.length === 1 ? '' : 's'} on the current page`;

    const fetchAccounts = useCallback(async () => {
        setLoadingAccounts(true);
        setError('');
        try {
            const res = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const data = res.data?.data ?? res.data ?? [];
            setAccounts(Array.isArray(data) ? data : []);
        } catch (err) {
            setAccounts([]);
            setError(err.response?.data?.message || 'Failed to load cashbook accounts.');
        } finally {
            setLoadingAccounts(false);
        }
    }, [authToken]);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedAccountId('');
        setAccounts([]);
        setScope('selected');
        setError('');
        setConfirmOpen(false);
        setPosting(false);
        fetchAccounts();
    }, [fetchAccounts, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (e.key === 'Escape' && !confirmOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [confirmOpen, isOpen, onClose]);

    if (!isOpen) return null;

    const handleBeginPost = () => {
        if (!selectedAccountId) {
            setError('Choose a cashbook account before posting.');
            return;
        }
        if (scope === 'all_matching' && matchingTotal === 0) {
            setError('No C2B rows match the current filters.');
            return;
        }
        setError('');
        setConfirmOpen(true);
    };

    const handleConfirmPost = async () => {
        setPosting(true);
        setError('');
        try {
            const payload = {
                cashbook_account_id: Number(selectedAccountId),
                apply_to_all: scope === 'all_matching',
            };

            if (scope === 'all_matching') {
                payload.filters = filters;
            } else {
                payload.ids = rows.map((row) => row.id);
            }

            await axios.post(
                `${rootUrl}/api/payments/c2b/bulk-post-to-cashbook`,
                payload,
                {
                    headers: { Authorization: `Bearer ${authToken}` },
                }
            );

            setConfirmOpen(false);
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to post selected C2B rows.');
        } finally {
            setPosting(false);
        }
    };

    return (
        <>
            <div
                className="fixed inset-0 z-40 bg-black bg-opacity-30"
                onClick={() => {
                    if (!confirmOpen) onClose();
                }}
            />

            <div className="fixed top-0 right-0 h-full z-50 w-full max-w-lg bg-[#faf8f6] shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
                    <div>
                        <p className="font-semibold text-gray-800 text-sm">Bulk Post to Cashbook</p>
                        <p className="text-xs text-gray-500">
                            {scope === 'all_matching'
                                ? `${matchingTotal} matching transaction${matchingTotal === 1 ? '' : 's'}`
                                : `${rows.length} selected transaction${rows.length === 1 ? '' : 's'}`
                            }
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
                        aria-label="Close bulk post modal"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5">
                    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4">
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-xs text-gray-500">{rowsInSummaryLabel}</span>
                            <span className="text-sm font-medium text-gray-800">{rowsToPostCount}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-gray-100">
                            <span className="text-xs text-gray-500">Batch total</span>
                            <span className="text-sm font-semibold text-gray-900">{formatMoney(batchTotalAmount)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2">
                            <span className="text-xs text-gray-500">Week key(s)</span>
                            <span className="text-xs text-right text-gray-800 break-all">{scopeWeekLabels}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-t border-gray-100 mt-1">
                            <span className="text-xs text-gray-500">Rows to post</span>
                            <span className="text-sm font-semibold text-gray-900">
                                {rowsToPostCount} {rowsToPostLabel}
                            </span>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4">
                        <label htmlFor="bulk-post-scope" className="block text-xs font-medium text-gray-600 mb-2">
                            Post scope
                        </label>
                        <Select
                            inputId="bulk-post-scope"
                            value={scopeOptions.find((option) => option.value === scope) || null}
                            onChange={(opt) => setScope(opt?.value || 'selected')}
                            options={scopeOptions}
                            styles={selectStyles}
                            className="mb-4"
                            classNamePrefix="react-select"
                            isSearchable={false}
                            isClearable={false}
                        />
                        <p className="text-xs text-gray-500 mb-4">
                            {scope === 'all_matching'
                                ? `This will post all matching transactions across all pages (${matchingTotal} matching rows). Car Post Status is ignored for cashbook posting, and already cashbook-posted rows are skipped.`
                                : 'This will post only the rows selected on the current page.'
                            }
                        </p>

                        <label htmlFor="cashbook-account" className="block text-xs font-medium text-gray-600 mb-2">
                            Cashbook account
                        </label>
                        <Select
                            id="cashbook-account"
                            inputId="cashbook-account"
                            value={selectedAccountOption}
                            onChange={(opt) => {
                                setSelectedAccountId(opt?.value || '');
                                setError('');
                            }}
                            options={accounts.map((account) => ({
                                value: String(account.id),
                                label: `${account.account_name}${account.account_type ? ` (${account.account_type})` : ''}`,
                            }))}
                            styles={selectStyles}
                            className="mb-2"
                            classNamePrefix="react-select"
                            isSearchable
                            isClearable
                            isDisabled={loadingAccounts}
                            placeholder={loadingAccounts ? 'Loading accounts…' : 'Select an account'}
                        />
                        <p className="text-xs text-gray-500 mt-2">
                            One cashbook entry will be created for the selected rows.
                        </p>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-2">
                        {rows.map((row) => (
                            <div key={row.id} className="bg-white rounded-lg border border-gray-100 px-4 py-3 flex items-start justify-between gap-4">
                                <div>
                                    <p className="font-mono text-sm font-medium text-gray-800">{row.trans_id}</p>
                                    <p className="text-xs text-gray-500">{row.bill_ref_raw || '—'}</p>
                                </div>
                                <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{formatMoney(row.amount)}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="px-5 py-4 border-t border-gray-200 bg-white flex items-center justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleBeginPost}
                        disabled={
                            loadingAccounts ||
                            rows.length === 0 ||
                            (scope === 'all_matching' && matchingTotal === 0)
                        }
                        className="px-4 py-2 rounded-lg bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Review and Post
                    </button>
                </div>
            </div>

            <ConfirmModal
                isOpen={confirmOpen}
                title={modalTitle}
                description={`${modalDescription} to ${selectedAccount?.account_name || 'the selected cashbook account'} as one batch entry?`}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirmPost}
                confirmText="Post Batch"
                cancelText="Back"
                loading={posting}
                error={error}
                variant="primary"
            />
        </>
    );
};

export default C2BBulkPostModal;
